import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Coins, ShoppingBag, AlertCircle, Lock, TrendingDown, Zap, FlaskConical } from 'lucide-react';
import { shopAPI } from '../utils/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const RARITY_STYLES = {
  common: { border: 'border-slate-200', bg: 'bg-white', badge: 'bg-slate-100 text-slate-600', glow: '' },
  rare: { border: 'border-amber-300', bg: 'bg-gradient-to-br from-amber-50 to-orange-50', badge: 'bg-amber-100 text-amber-700', glow: 'shadow-amber-200/40' },
  legendary: { border: 'border-violet-400', bg: 'bg-gradient-to-br from-violet-50 to-fuchsia-50', badge: 'bg-violet-100 text-violet-700', glow: 'shadow-violet-300/40' }
};

const PokeShop = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [coins, setCoins] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [levelUpData, setLevelUpData] = useState(null);

  const loadCatalog = useCallback(async () => {
    try {
      const response = await shopAPI.getCatalog();
      setCatalog(response.data.items || []);
      setCoins(response.data.coins || 0);
    } catch (error) {
      console.error('Failed to load shop:', error);
      toast.error('Erro ao carregar a loja');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const fireLevelUpConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#3B82F6'];
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  };

  const handlePurchase = async (item) => {
    if (coins < item.price) {
      toast.error(`Coins insuficientes! Voce tem ${coins} e precisa de ${item.price}.`);
      return;
    }
    if (item.remaining_stock !== null && item.remaining_stock <= 0) {
      toast.error('Estoque semanal esgotado!');
      return;
    }

    setPurchasing(item.item_type);
    try {
      const response = await shopAPI.purchase(item.item_type);
      const data = response.data;
      setCoins(data.new_coins);

      // Instant XP items - special feedback
      if (data.instant) {
        if (data.level_up) {
          fireLevelUpConfetti();
          setLevelUpData({
            levels: data.levels_gained,
            newLevel: data.new_level,
            xp: data.xp_gained,
            evolution: data.evolution,
            emoji: data.item.emoji
          });
          setTimeout(() => setLevelUpData(null), 4000);
        } else {
          setPurchaseSuccess({ ...data.item, instantXp: data.xp_gained });
          setTimeout(() => setPurchaseSuccess(null), 2500);
        }
        toast.success(`+${data.xp_gained} AURA aplicada!`, {
          description: data.level_up
            ? `Subiu ${data.levels_gained} nivel${data.levels_gained > 1 ? 'is' : ''}! Agora nivel ${data.new_level}`
            : 'AURA adicionada direto na barra!'
        });
        if (data.evolution) {
          setTimeout(() => {
            toast.success(`Seu Pokemon evoluiu para ${data.evolution.monster_name}!`, { duration: 5000 });
          }, 1500);
        }
      } else {
        // Regular inventory items
        setPurchaseSuccess(data.item);
        setTimeout(() => setPurchaseSuccess(null), 2500);
        toast.success(`${item.name} adquirido!`, {
          description: item.item_type === 'transmutation_sphere'
            ? 'Seu Mestre foi notificado sobre a troca!'
            : 'O item foi adicionado a sua Mochila.'
        });
      }
      loadCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao comprar item');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Level Up Overlay */}
      {levelUpData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative z-10 text-center" style={{ animation: 'lvlUpEntry 0.6s ease-out' }}>
            <div className="bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-500 rounded-3xl p-10 shadow-2xl border-4 border-yellow-300" style={{ boxShadow: '0 0 60px rgba(255,215,0,0.5)' }}>
              <div className="text-6xl mb-3" style={{ animation: 'shopSpin 1s ease-out' }}>{levelUpData.emoji}</div>
              <p className="text-white font-outfit font-black text-3xl mb-1" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                LEVEL UP!
              </p>
              <p className="text-yellow-100 font-outfit font-bold text-xl">
                +{levelUpData.xp} AURA &middot; Nivel {levelUpData.newLevel}
              </p>
              {levelUpData.evolution && (
                <div className="mt-3 bg-white/20 rounded-xl px-4 py-2 backdrop-blur-sm">
                  <p className="text-white font-bold text-sm">
                    Evolucao! {levelUpData.evolution.old_name} &rarr; {levelUpData.evolution.monster_name}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Purchase Success Overlay (regular items) */}
      {purchaseSuccess && !levelUpData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 bg-white rounded-3xl p-8 shadow-2xl text-center" style={{ animation: 'shopBounceIn 0.5s ease-out' }}>
            <div className="text-5xl mb-3" style={{ animation: 'shopSpin 0.8s ease-out' }}>
              {purchaseSuccess.emoji}
            </div>
            <p className="font-outfit font-bold text-xl text-slate-800">{purchaseSuccess.name}</p>
            {purchaseSuccess.instantXp ? (
              <p className="text-sm text-cyan-600 font-bold mt-1">+{purchaseSuccess.instantXp} AURA aplicada!</p>
            ) : (
              <p className="text-sm text-green-600 font-bold mt-1">Adicionado a Mochila!</p>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button data-testid="back-to-dashboard-btn" onClick={() => navigate('/student/dashboard')} variant="ghost" className="flex items-center gap-2 text-slate-600">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Button>
          <div className="flex items-center gap-3">
            <div data-testid="coin-balance" className="flex items-center gap-2 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl px-4 py-2">
              <Coins className="w-5 h-5 text-amber-500" />
              <span className="font-outfit font-black text-xl text-amber-700">{coins}</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-3">
            <ShoppingBag className="w-8 h-8 text-amber-500" />
            <h1 className="text-4xl font-outfit font-black text-slate-900">PokeShop</h1>
          </div>
          <p className="text-slate-500 font-nunito">Gaste suas Coins em itens para sua aventura!</p>
        </div>

        {/* Item Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {catalog.map((item) => {
            const canAfford = coins >= item.price;
            const outOfStock = item.remaining_stock !== null && item.remaining_stock <= 0;
            const isPurchasing = purchasing === item.item_type;
            const style = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
            const isInstant = item.effect_type === 'instant_xp';

            return (
              <div
                key={item.item_type}
                data-testid={`shop-item-${item.item_type}`}
                className={`rounded-2xl border-2 p-5 transition-all relative overflow-hidden ${style.bg} ${style.border} ${
                  canAfford && !outOfStock ? `hover:shadow-xl ${style.glow} hover:-translate-y-1` : 'opacity-80'
                }`}
              >
                {/* Rarity badge + instant badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${style.badge}`}>
                      {item.rarity === 'common' ? 'Comum' : item.rarity === 'rare' ? 'Raro' : 'Lendario'}
                    </span>
                    {isInstant && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-cyan-100 text-cyan-700 flex items-center gap-0.5">
                        <Zap className="w-2.5 h-2.5" /> Uso Imediato
                      </span>
                    )}
                  </div>
                  {item.remaining_stock !== null && (
                    <span data-testid={`stock-${item.item_type}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      item.remaining_stock > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {item.remaining_stock > 0 ? `${item.remaining_stock} restantes` : 'Esgotado'}
                    </span>
                  )}
                </div>

                {/* Item emoji + name */}
                <div className="text-center mb-4">
                  <div className="text-5xl mb-2">{item.emoji}</div>
                  <h3 className="font-outfit font-bold text-lg text-slate-800">{item.name}</h3>
                  <p className="text-xs text-slate-500 font-nunito mt-1">{item.description}</p>
                  {isInstant && (
                    <p className="text-[10px] text-cyan-600 font-bold font-nunito mt-1.5 flex items-center justify-center gap-1">
                      <FlaskConical className="w-3 h-3" />
                      Sobe sua barra de AURA instantaneamente!
                    </p>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center justify-center gap-1.5 mb-4">
                  <Coins className="w-4 h-4 text-amber-500" />
                  <span className="font-outfit font-black text-2xl text-amber-700">{item.price}</span>
                </div>

                {/* Buy button */}
                <Button
                  data-testid={`buy-${item.item_type}`}
                  onClick={() => handlePurchase(item)}
                  disabled={!canAfford || outOfStock || isPurchasing}
                  className={`w-full h-11 rounded-xl font-bold transition-all ${
                    outOfStock
                      ? 'bg-slate-200 text-slate-400'
                      : canAfford
                        ? `bg-gradient-to-r ${item.color} text-white hover:shadow-lg`
                        : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  {isPurchasing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {isInstant ? 'Aplicando...' : 'Comprando...'}
                    </span>
                  ) : outOfStock ? (
                    <span className="flex items-center gap-2"><Lock className="w-4 h-4" /> Esgotado</span>
                  ) : !canAfford ? (
                    <span className="flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Coins insuficientes</span>
                  ) : isInstant ? (
                    <span className="flex items-center gap-2"><Zap className="w-4 h-4" /> Usar Agora</span>
                  ) : (
                    <span className="flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Comprar</span>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Info section */}
        <div className="mt-10 max-w-2xl mx-auto bg-amber-50 rounded-2xl p-5 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-800 text-sm">Como ganhar Coins?</h4>
              <p className="text-xs text-amber-700 mt-1">
                Complete missoes para ganhar Coins! Quanto mais questoes acertar, mais Coins recebe.
                Itens com estoque limitado sao resetados toda semana.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shopBounceIn { 0% { transform: scale(0.3); opacity: 0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes shopSpin { 0% { transform: rotate(0deg) scale(0.5); } 100% { transform: rotate(360deg) scale(1); } }
        @keyframes lvlUpEntry { 0% { transform: scale(0.2) rotate(-10deg); opacity: 0; } 50% { transform: scale(1.15) rotate(3deg); } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default PokeShop;
